import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../components/services/auth.service';
import { BillingService } from '../components/services/billing.service';

export interface PlanFeatureGroup {
  heading: string;
  items: string[];
}

export interface PlanFaq {
  q: string;
  a: string;
}

export interface PlanData {
  id: string;
  name: string;
  price: string;
  period: string;
  tagline: string;
  description: string;
  ctaLabel: string;
  ctaRoute: string;
  accentColor: string;
  badge?: string;
  featureGroups: PlanFeatureGroup[];
  whoFor: string[];
  faqs: PlanFaq[];
}

export const PLANS: Record<string, PlanData> = {
  free: {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    tagline: 'Get started at no cost',
    description: 'The Free plan is built for solo contractors and independent tradespeople who want to digitize their project tracking without any upfront commitment.',
    ctaLabel: 'Get Started Free',
    ctaRoute: '/auth',
    accentColor: '#457b9d',
    featureGroups: [
      {
        heading: 'Team & Users',
        items: ['1 team member', 'Personal account dashboard', 'Role-based access (provider or customer)']
      },
      {
        heading: 'Project Management',
        items: ['Up to 3 active projects', 'Basic task management', 'Project progress tracking', 'Photo & drawing uploads']
      },
      {
        heading: 'Bidding',
        items: ['Up to 5 bids / month', 'Basic bid form with contacts', 'Status tracking (Pending, Submitted, Won, Lost)']
      },
      {
        heading: 'Support',
        items: ['Community forum access', 'Help center documentation', 'Email response within 5 business days']
      }
    ],
    whoFor: [
      'Solo tradespeople just starting out',
      'Contractors trying the platform before upgrading',
      'Independent handymen managing a few jobs at once'
    ],
    faqs: [
      { q: 'Is the Free plan really free?', a: 'Yes — no credit card required. You can use the Free plan indefinitely with no time limit.' },
      { q: 'What happens when I hit 3 projects?', a: 'You\'ll need to complete or archive an existing project, or upgrade to Pro to unlock unlimited projects.' },
      { q: 'Can I upgrade later?', a: 'Absolutely. You can upgrade to Pro or Enterprise at any time from your account settings.' }
    ]
  },

  pro: {
    id: 'pro',
    name: 'Pro',
    price: '$29',
    period: 'per month',
    tagline: 'For growing teams and serious contractors',
    description: 'The Pro plan is designed for small contracting companies that need real collaboration tools, unlimited projects, and smarter resource management to stay profitable.',
    ctaLabel: 'Start 14-Day Free Trial',
    ctaRoute: '/plans/pro/checkout',
    accentColor: '#457b9d',
    badge: 'Most Popular',
    featureGroups: [
      {
        heading: 'Team & Users',
        items: ['Up to 5 team members', 'Role-based permissions', 'Shared project workspace', 'Team activity feed']
      },
      {
        heading: 'Project Management',
        items: ['Unlimited active projects', 'Advanced task management with assignments', 'Labor cost tracking', 'Material & equipment tracking', 'Project bids with multiple contacts', 'Project schedules & milestones', 'Resource planning by project']
      },
      {
        heading: 'Reporting & Insights',
        items: ['Labor cost vs. billed amount', 'Gross profit per project', 'Worker hours breakdown', 'Project progress dashboard']
      },
      {
        heading: 'Support',
        items: ['Priority email support', 'Response within 1 business day', 'Onboarding walkthrough', 'Access to all future Pro features']
      }
    ],
    whoFor: [
      'Small contracting companies (2–5 employees)',
      'Contractors managing multiple simultaneous jobs',
      'Teams that need to track labor costs and margins',
      'Businesses ready to move beyond spreadsheets'
    ],
    faqs: [
      { q: 'Is there a free trial?', a: 'Yes — try Pro free for 14 days, no credit card needed. You\'ll be notified before any charge.' },
      { q: 'Can I add more than 5 members?', a: 'If you need more than 5 team members, the Enterprise plan supports unlimited users.' },
      { q: 'What if I cancel?', a: 'You can cancel at any time. You\'ll keep Pro access until the end of your billing period, then revert to the Free plan.' },
      { q: 'Are bids included?', a: 'Yes — unlimited bids with the full contact management feature, status tracking, and bid history.' }
    ]
  },

  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$99',
    period: 'per month',
    tagline: 'Built for large contracting firms',
    description: 'The Enterprise plan gives large contracting businesses the full suite of tools they need — unlimited team members, advanced analytics, custom integrations, and a dedicated account manager.',
    ctaLabel: 'Contact Sales',
    ctaRoute: '/contactus',
    accentColor: '#1D3557',
    featureGroups: [
      {
        heading: 'Team & Users',
        items: ['Unlimited team members', 'Custom roles & permissions', 'Department-level access controls', 'SSO / Active Directory integration (coming soon)']
      },
      {
        heading: 'Project Management',
        items: ['Everything in Pro', 'Unlimited projects & bids', 'Multi-project resource planning', 'Cross-project labor analytics', 'Custom project statuses & workflows', 'Bulk import / export']
      },
      {
        heading: 'Reporting & Analytics',
        items: ['Advanced profit & loss by project', 'Company-wide labor dashboard', 'Custom report builder', 'Export to CSV / Excel', 'API access for custom integrations']
      },
      {
        heading: 'Support & SLA',
        items: ['Dedicated account manager', '24/7 priority phone & email support', 'Response within 2 hours', 'Custom onboarding & training', 'Quarterly business reviews']
      }
    ],
    whoFor: [
      'Large general contractors with 10+ employees',
      'Businesses managing $1M+ in annual project volume',
      'Companies that need compliance & audit trails',
      'Firms integrating with existing ERP or accounting systems'
    ],
    faqs: [
      { q: 'How do I get started with Enterprise?', a: 'Click "Contact Sales" and our team will schedule a demo and tailor a plan to your business needs.' },
      { q: 'Can I negotiate pricing?', a: 'Yes — Enterprise pricing is flexible for annual commitments or high-volume teams. Talk to sales.' },
      { q: 'Is there a setup fee?', a: 'There is no standard setup fee. Custom onboarding packages may be available for complex integrations.' },
      { q: 'What kind of SLA do you offer?', a: 'Enterprise customers get a 99.9% uptime SLA with guaranteed response times and escalation paths.' }
    ]
  }
};

@Component({
  selector: 'app-plan-detail',
  templateUrl: './plan-detail.component.html',
  styleUrls: ['./plan-detail.component.css']
})
export class PlanDetailComponent implements OnInit {
  plan: PlanData | null = null;
  openFaq: number | null = null;

  currentTier = 'free';
  currentStatus: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private billingService: BillingService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id') || '';
      this.plan = PLANS[id] || null;
      this.openFaq = null;
    });

    const user = this.authService.getCurrentUser();
    if (user) {
      this.billingService.getSubscription(user.id).subscribe({
        next: sub => {
          this.currentTier = sub.tier || 'free';
          this.currentStatus = sub.status;
        },
        error: () => {}
      });
    }
  }

  // Only Pro is self-serve (checkout); Free (sign up) and Enterprise (sales-led
  // "Contact Sales") keep their navigate-away CTA.
  get isSelfServeTier(): boolean {
    return this.plan?.id === 'pro';
  }

  get isCurrentPlan(): boolean {
    return !!this.plan && this.plan.id === this.currentTier
      && (this.plan.id === 'free' || this.currentStatus === 'active' || this.currentStatus === 'trialing');
  }

  get ctaButtonLabel(): string {
    if (!this.plan) return '';
    if (this.isSelfServeTier && this.isCurrentPlan) return 'Current Plan';
    return this.plan.ctaLabel;
  }

  toggleFaq(i: number): void {
    this.openFaq = this.openFaq === i ? null : i;
  }

  goBack(): void {
    this.router.navigate(['/home']);
  }

  goToPlan(id: string): void {
    this.router.navigate(['/plans', id]);
  }

  cta(): void {
    if (!this.plan || (this.isSelfServeTier && this.isCurrentPlan)) { return; }
    // Pro goes through /plans/pro/checkout, which handles sign-in/sign-up (via the
    // auth guard's returnUrl), card collection, and the trial — all in one flow.
    this.router.navigateByUrl(this.plan.ctaRoute);
  }

  get otherPlans(): PlanData[] {
    if (!this.plan) { return []; }
    return Object.values(PLANS).filter(p => p.id !== this.plan!.id);
  }
}
